import { useQuery } from '@tanstack/react-query'
import { Heart, Search, Users, Crown, Bell, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { profileApi } from '../../api/profile'
import { matchesApi } from '../../api/matches'
import { notificationsApi } from '../../api/notifications'
import ProfileCard from '../../components/ProfileCard'

export default function DashboardPage() {
  const { data: profile } = useQuery({
    queryKey: ['my-profile'],
    queryFn: () => profileApi.getMyProfile().then((r) => r.data.data),
  })

  const { data: suggestions, isLoading } = useQuery({
    queryKey: ['suggestions'],
    queryFn: () => matchesApi.getSuggestions(0, 6).then((r) => r.data.data),
  })

  const { data: unreadCount } = useQuery({
    queryKey: ['notif-count'],
    queryFn: () => notificationsApi.getUnreadCount().then((r) => r.data.data?.count ?? 0),
  })

  const firstName = profile?.fullName?.split(' ')[0] ?? ''
  const completePct = profile?.profileCompletePct ?? 0

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  return (
    <div className="max-w-5xl mx-auto">
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 pt-7 pb-4">
        <div>
          <p className="kicker">{greeting}</p>
          <h1 className="font-display font-semibold text-[30px] text-ink-900 mt-1">
            {firstName ? `Namaste, ${firstName}` : 'Namaste'}
          </h1>
        </div>
        <Link to="/notifications" className="relative w-10 h-10 rounded-full bg-ivory-25 border border-ivory-200 shadow-soft flex items-center justify-center text-ink-400 hover:text-maroon-700 transition-colors">
          <Bell size={18} strokeWidth={1.75} />
          {unreadCount != null && unreadCount > 0 && (
            <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-maroon-700 ring-1 ring-gold-500/60" />
          )}
        </Link>
      </div>

      <div className="px-4 pb-8 space-y-6">

        {/* Profile completion card — only if incomplete */}
        {profile && completePct < 80 && (
          <Link
            to="/profile/edit"
            className="block rounded-2xl p-5 shadow-btn ring-1 ring-inset ring-gold-500/40 hover:shadow-float transition-shadow"
            style={{ background: 'linear-gradient(135deg, #7A1B2E, #4E0F1F)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-champagne-bright font-display font-semibold text-lg">Complete your profile</p>
                <p className="text-champagne/70 text-xs mt-0.5">A complete profile receives three times more interest</p>
              </div>
              <ChevronRight size={20} className="text-gold-500 flex-shrink-0" />
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 bg-white/15 rounded-full overflow-hidden">
                <div className="h-full bg-gold-500 rounded-full" style={{ width: `${completePct}%` }} />
              </div>
              <span className="text-champagne font-bold text-sm">{completePct}%</span>
            </div>
          </Link>
        )}

        {/* Quick actions */}
        <div className="grid grid-cols-4 gap-2.5">
          {[
            { to: '/matches',      icon: Search, label: 'Browse'    },
            { to: '/interests',    icon: Heart,  label: 'Interests' },
            { to: '/shortlist',    icon: Users,  label: 'Saved'     },
            { to: '/subscription', icon: Crown,  label: 'Premium'   },
          ].map(({ to, icon: Icon, label }) => (
            <Link key={to} to={to} className="flex flex-col items-center gap-2 group">
              <div className="w-14 h-14 rounded-full bg-ivory-25 border border-ivory-200 flex items-center justify-center shadow-soft group-hover:border-gold-500/50 group-hover:shadow-card transition-all">
                <Icon size={21} strokeWidth={1.5} className="text-maroon-700" />
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-400">{label}</span>
            </Link>
          ))}
        </div>

        {/* Suggested matches */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-title flex-1">Today's Picks</h2>
            <Link to="/matches" className="text-xs font-semibold uppercase tracking-[0.12em] text-maroon-700 flex items-center gap-0.5 hover:underline ml-4">
              See all <ChevronRight size={13} />
            </Link>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-ivory-100 rounded-2xl h-64 animate-pulse" />
              ))}
            </div>
          ) : suggestions?.length ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {suggestions.map((match) => (
                <ProfileCard key={match.profile.id} match={match} />
              ))}
            </div>
          ) : (
            <div className="card p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-ivory-100 border border-gold-500/30 flex items-center justify-center mx-auto mb-3">
                <Heart size={26} strokeWidth={1.5} className="text-maroon-700/50" />
              </div>
              <p className="font-display font-semibold text-lg text-ink-900 mb-1">No suggestions yet</p>
              <p className="text-sm text-ink-400 mb-4">Complete your profile to start seeing matches</p>
              <Link to="/profile/edit" className="btn-primary text-sm py-2.5 px-5">
                Complete Profile
              </Link>
            </div>
          )}
        </div>

      </div>
    </div>
  )
}
